import { QuoteType } from "api/rest/quote-requests";
import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import i18next from "i18next";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";
import { UnloadingDetailsEnum } from "types/UnloadingDetails";
import { z } from "zod";

const addressSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullable().optional(),
  address_1: z.string().nullable().optional(),
  address_2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  country_name: z.string().nullable().optional(),
  province_code: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  google_place_id: z.string().nullable().optional(),
  google_place_description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
});

const stopSchema = z.object({
  address: addressSchema.refine((val) => val != null, {
    message: i18next.t("quoteRequest:LocationStep.addressRequired"),
  }),
});

const nullableStopSchema = z.object({
  address: addressSchema.nullable().optional(),
});

const baseRoutingStepSchema = z.object({
  incoterms: z.nativeEnum(IncotermsEnum),
  cargo_ready_date: z.string(),
  target_delivery_date: z.string().optional(),
  carrierRoutingPrefNotes: z.string().optional(),
});

const p2pRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.P2P),
  originStop: nullableStopSchema.optional(),
  originPort: stopSchema,
  destinationPort: stopSchema,
  destinationStop: nullableStopSchema.optional(),
});

const d2dRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.D2D),
  originStop: stopSchema,
  originPort: nullableStopSchema.optional(),
  destinationPort: nullableStopSchema.optional(),
  destinationStop: stopSchema,
});

const p2dRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.P2D),
  originStop: nullableStopSchema.optional(),
  originPort: stopSchema,
  destinationPort: nullableStopSchema.optional(),
  destinationStop: stopSchema,
});

const d2pRoutingStepSchema = z.object({
  routing_type: z.literal(RoutingTypeEnum.D2P),
  originStop: stopSchema,
  originPort: nullableStopSchema.optional(),
  destinationPort: stopSchema,
  destinationStop: nullableStopSchema.optional(),
});

export const routingSchema = z
  .discriminatedUnion(
    "routing_type",
    [p2pRoutingStepSchema, d2dRoutingStepSchema, p2dRoutingStepSchema, d2pRoutingStepSchema],
    {
      errorMap: (issue) => {
        if (issue.code === "invalid_union_discriminator") {
          return { message: "A routing type is required" };
        }
        return { message: issue.message ?? "" };
      },
    }
  )
  .and(baseRoutingStepSchema);

export type StopSchema = z.infer<typeof stopSchema>;
export type RoutingSchema = z.infer<typeof routingSchema>;

export type ContainserSchema = z.infer<typeof containersSchema>;

export const quotePreferencesSchema = routingSchema.and(
  z.object({
    quoteTypes: z.array(z.nativeEnum(QuoteType)).min(1, { message: "At least one quote type is required" }),
    directSailingRequired: z.boolean().optional(),
    capacityGuaranteeRequired: z.boolean().optional(),
    carrierRoutingPrefNotes: z.string().optional(),
  })
);

export const containerSchema = z.object({
  type: z.string().min(1, { message: "Container type is required" }),
  size: z.string().min(1, { message: "Container size is required" }),
  // There is currently no concept of container count on the BE. We will have to add this in later if it is needed
  // count: z
  //   .union([z.string(), z.number()])
  //   .transform((val) => (typeof val === "string" ? parseInt(val, 10) : val))
  //   .pipe(
  //     z
  //       .number()
  //       .min(1, { message: "Number of containers must be at least 1" })
  //       .max(100, { message: "Maximum 100 containers allowed" })
  //   ),
  min_temp: z
    .union([z.string(), z.number(), z.undefined()])
    .transform((val) =>
      typeof val === "string" && val !== "" ? parseFloat(val) : typeof val === "number" ? val : undefined
    )
    .optional()
    .nullable(),
  max_temp: z
    .union([z.string(), z.number(), z.undefined()])
    .transform((val) =>
      typeof val === "string" && val !== "" ? parseFloat(val) : typeof val === "number" ? val : undefined
    )
    .optional()
    .nullable(),
  requires_genset: z.boolean().optional(),
  weight: z
    .union([z.string(), z.number(), z.undefined()])
    .transform((val) =>
      typeof val === "string" && val !== "" ? parseFloat(val) : typeof val === "number" ? val : undefined
    )
    .optional()
    .nullable(),
  volume: z
    .union([z.string(), z.number(), z.undefined()])
    .transform((val) =>
      typeof val === "string" && val !== "" ? parseFloat(val) : typeof val === "number" ? val : undefined
    )
    .optional()
    .nullable(),
});

export type ContainerSchema = z.infer<typeof containerSchema>;

export const containersSchema = quotePreferencesSchema.and(
  z.object({
    containers: z.array(containerSchema).min(1, { message: "At least one container is required" }),
  })
);

export type QuotePreferencesSchema = z.infer<typeof quotePreferencesSchema>;

export const baseShipmentDetailsSchema = containersSchema.and(
  z.object({
    referenceNumber: z.string().optional(),
    internalReferenceNumber: z.string().optional().nullable(),
    deadlineRespondAt: z.string().optional().nullable(),
    unloadingDetails: z.nativeEnum(UnloadingDetailsEnum),
    isPalletized: z.boolean(),
    packagingType: z.union([z.nativeEnum(ShipmentPackagingType), z.undefined()]),
    packingCount: z.union([
      z.number().min(1, i18next.t("quoteRequest:ShipmentDetailsStep.packingCountMin")),
      z.undefined(),
    ]),
    palletCount: z.union([
      z.number().min(1, i18next.t("quoteRequest:ShipmentDetailsStep.palletCountMin")),
      z.undefined(),
    ]),
    isOverweight: z.boolean().optional(),
    isHazardous: z.boolean().optional(),
    hazardousGoodsDetails: z.string().optional(),
    insuranceRequired: z.boolean().optional(),
    goodsValue: z.number().min(0).optional(),
    additionalNotes: z.string().optional(),
  })
);

export const shipmentDetailsSchema = z
  .discriminatedUnion("includeCustomsClearance", [
    z.object({
      includeCustomsClearance: z.literal(true),
      commodities: z.string().min(1, i18next.t("quoteRequest:ShipmentDetailsStep.commoditiesRequired")),
    }),
    z.object({
      includeCustomsClearance: z.literal(false),
      commodities: z.string().optional(),
    }),
    z.object({
      includeCustomsClearance: z.undefined(),
      commodities: z.string().optional(),
    }),
  ])
  .and(
    z.discriminatedUnion("isHazardous", [
      z.object({
        isHazardous: z.literal(true),
        hazardousGoodsDetails: z.string(),
      }),
      z.object({
        isHazardous: z.literal(false),
        hazardousGoodsDetails: z.string().optional(),
      }),
    ])
  )
  .and(baseShipmentDetailsSchema);

export type ShipmentDetailsSchema = z.infer<typeof shipmentDetailsSchema>;

export const partnersSchema = shipmentDetailsSchema.and(
  z.object({
    partners: z.object({ contact_id: z.number() }).array().min(1, { message: "At least one partner must be selected" }),
  })
);

export type PartnersScheam = z.infer<typeof partnersSchema>;

export const teamSchema = partnersSchema.and(
  z.object({
    teammates: z.object({ contact_id: z.number() }).array().optional(),
  })
);

export type TeamSchema = z.infer<typeof teamSchema>;

export type FinalSchema = TeamSchema;
